import React, { useEffect, useState, useRef } from 'react'
import { CheckCircle, Clock, ArrowRight, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useUsage } from '../../contexts/UsageContext'
import { config } from '../../config/env'
import { GuideDownload } from '../../components/GuideDownload'
import { PlaybookModal } from '../../components/modals/PlaybookModal'
import { supabase } from '../../lib/supabase'
import { hashEmail } from './helpers'
import { getDeviceInfo, hasPaymentInfoAvailable } from '../../utils/deviceDetection'
import { getPurchaseEventId } from '../../utils/eventIdManager'
import { useConfirmedUsage } from './useConfirmedUsage'
import { signInPathForDestination } from '../../lib/authRedirect'

interface SuccessPageProps {
  onBack: () => void
  onStartClipping: () => void
  onShowDashboard: () => void
}

export const SuccessPage: React.FC<SuccessPageProps> = ({ onStartClipping, onShowDashboard }) => {
  const [showPlaybookModal, setShowPlaybookModal] = useState(false)
  const [hasTrackedPurchase, setHasTrackedPurchase] = useState(false)
  const [purchaseEventId, setPurchaseEventId] = useState<string | null>(null)
  const isTrackingRef = useRef(false)
  const { user } = useAuth()
  const { usage } = useUsage()
  const confirmation = useConfirmedUsage()
  const active = confirmation.usage?.subscription_status === 'active'
  const sourceMinutes = confirmation.usage?.credits_remaining

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const session = urlParams.get('session_id')
    const isPdfPurchase = urlParams.get('pdf') === 'purchased'

    if (session && !hasTrackedPurchase && !isTrackingRef.current) {
      const trackPurchaseEvent = async () => {
        isTrackingRef.current = true
        // Facebook & Snapchat Purchase Event
        try {

          const response = await fetch(`${config.API_BASE_URL}/api/stripe/session-details/${session}`)

          if (!response.ok) {
            throw new Error(`Failed to fetch session details: ${response.status}`)
          }

          const sessionDetails = await response.json()

          // CRITICAL: Use event_id from session metadata for deduplication
          // This is the SAME event_id that was generated during InitiateCheckout and will be
          // used by the backend CAPI call from the webhook. Both Pixel and CAPI MUST use
          // the same event_id to enable Snapchat's 48-hour deduplication window.
          const eventIdFromSession = sessionDetails.event_id
          let finalEventId: string

          if (eventIdFromSession) {
            finalEventId = eventIdFromSession
            setPurchaseEventId(eventIdFromSession)
            if (process.env.NODE_ENV === 'development') {
              console.log(`🔑 Using event_id from session metadata (matches CAPI): ${eventIdFromSession}`)
            }
          } else {
            // Fallback: Generate new Purchase event ID if not in session
            // WARNING: This will NOT match CAPI if webhook fires, resulting in 0% deduplication
            finalEventId = getPurchaseEventId()
            setPurchaseEventId(finalEventId)
            console.warn(`⚠️ No event_id in session metadata - Pixel/CAPI deduplication will fail! Using: ${finalEventId}`)
          }

          // Track with Facebook Pixel
          if (typeof (window as any).fbq !== 'undefined') {
            const customerDetails = sessionDetails.customer_details || {}

            // Build advanced matching parameters (normalized as per Facebook spec)
            const advancedMatching: any = {}

            if (customerDetails.email) {
              advancedMatching.em = customerDetails.email.toLowerCase().trim()
            }
            if (customerDetails.phone) {
              advancedMatching.ph = customerDetails.phone.replace(/\D/g, '')
            }
            if (customerDetails.first_name) {
              advancedMatching.fn = customerDetails.first_name.toLowerCase().trim()
            }
            if (customerDetails.last_name) {
              advancedMatching.ln = customerDetails.last_name.toLowerCase().trim()
            }
            if (customerDetails.city) {
              advancedMatching.ct = customerDetails.city.toLowerCase().replace(/[^a-z0-9]/g, '')
            }
            if (customerDetails.state) {
              advancedMatching.st = customerDetails.state.toLowerCase().trim()
            }
            if (customerDetails.zip) {
              advancedMatching.zp = customerDetails.zip.toLowerCase().trim()
            }
            if (customerDetails.country) {
              advancedMatching.country = customerDetails.country.toLowerCase().trim()
            }

            const purchaseData = isPdfPurchase ? {
              value: sessionDetails.amount,
              currency: sessionDetails.currency,
              contents: [{ id: sessionDetails.price_id || 'pdf_guide', quantity: 1 }],
              content_type: 'product',
              order_id: sessionDetails.order_id || sessionDetails.session_id,
              ...advancedMatching
            } : {
              value: sessionDetails.amount,
              currency: sessionDetails.currency,
              contents: [{ id: sessionDetails.plan_id, quantity: 1 }],
              content_type: 'product',
              order_id: sessionDetails.order_id,
              subscription_interval: sessionDetails.subscription_interval,
              ...advancedMatching
            };

            (window as any).fbq('track', 'Purchase', purchaseData, { eventID: finalEventId })

            if (process.env.NODE_ENV === 'development') {
              console.log(`✅ Facebook Pixel Purchase tracked ${isPdfPurchase ? '(PDF)' : '(Subscription)'} with Stripe data and advanced matching`)
              console.log('📊 Advanced matching params:', Object.keys(advancedMatching))
            }
          }

          // Track with Snap Pixel
          if (typeof (window as any).snaptr !== 'undefined') {
            // Get device information for enhanced tracking
            const deviceInfo = getDeviceInfo()
            const paymentInfoAvailable = hasPaymentInfoAvailable()

            // Prepare user data with hashing if available
            let snapUserData = {}
            if (user?.email) {
              const hashedEmail = await hashEmail(user.email)
              snapUserData = {
                user_email: user.email,  // Snap SDK will hash automatically
                user_hashed_email: hashedEmail || undefined,
                // Add device information
                device_model: deviceInfo.device_model,
                os_version: deviceInfo.os_version,
                user_agent: deviceInfo.user_agent
              }
            }

            // Format purchase data for Snap with official Snapchat parameters
            // IMPORTANT: client_dedup_id MUST be in the event data object, not just options
            const snapPurchaseData: any = isPdfPurchase ? {
              price: sessionDetails.amount,
              currency: sessionDetails.currency || 'USD',
              transaction_id: sessionDetails.order_id || sessionDetails.session_id,
              item_ids: [sessionDetails.price_id || 'pdf_guide'],
              item_category: 'digital_product',
              number_items: 1,
              content_name: 'TopClip PDF Marketing Guide',
              content_ids: [sessionDetails.price_id || 'pdf_guide'],
              content_type: 'product',
              status: 'new',
              event_tag: 'pdf_purchase',
              client_dedup_id: finalEventId,  // CRITICAL: Must be in event data for deduplication
              ...snapUserData
            } : {
              price: sessionDetails.amount,
              currency: sessionDetails.currency || 'USD',
              transaction_id: sessionDetails.order_id || sessionDetails.session_id,
              item_ids: [sessionDetails.plan_id || sessionDetails.price_id],
              item_category: 'subscription',
              number_items: 1,
              content_name: `TopClip ${(sessionDetails.plan_id || 'starter').charAt(0).toUpperCase() + (sessionDetails.plan_id || 'starter').slice(1)} Plan`,
              content_ids: [sessionDetails.plan_id || sessionDetails.price_id],
              content_type: 'product',
              status: 'new',
              event_tag: `${sessionDetails.subscription_interval || 'monthly'}_subscription`,
              client_dedup_id: finalEventId,  // CRITICAL: Must be in event data for deduplication
              ...snapUserData
            };

            // Track PURCHASE event with Snap Pixel
            (window as any).snaptr('track', 'PURCHASE', snapPurchaseData);

            if (process.env.NODE_ENV === 'development') {
              console.log(`📸 Snap Pixel PURCHASE tracked ${isPdfPurchase ? '(PDF)' : '(Subscription)'}:`, {
                price: snapPurchaseData.price,
                currency: snapPurchaseData.currency,
                transaction_id: snapPurchaseData.transaction_id,
                item_ids: snapPurchaseData.item_ids,
                client_dedup_id: snapPurchaseData.client_dedup_id
              })
            }
          }

          setHasTrackedPurchase(true)

        } catch (error) {
          console.warn('❌ Failed to get real session data, using fallback:', error)

          if (!isPdfPurchase && usage?.plan && usage.plan !== 'free') {
            let fallbackValue = 0
            switch(usage.plan.toLowerCase()) {
              case 'hobby': fallbackValue = 9.00; break
              case 'starter': fallbackValue = 19.00; break
              case 'expert': fallbackValue = 34.00; break
              default: fallbackValue = 19.00
            }

            // Generate fallback event ID if needed
            const fallbackEventId = purchaseEventId || getPurchaseEventId()

            // Track with Facebook Pixel (fallback)
            if (typeof (window as any).fbq !== 'undefined') {
              ;(window as any).fbq('track', 'Purchase', {
                value: fallbackValue,
                currency: 'USD',
                contents: [{ id: usage.plan.toLowerCase(), quantity: 1 }],
                content_type: 'product',
                order_id: session,
                subscription_interval: 'annual'
              }, { eventID: fallbackEventId })

              if (process.env.NODE_ENV === 'development') {
                console.log('✅ Facebook Pixel Purchase tracked (fallback)')
              }
            }

            // Track with Snap Pixel (fallback)
            if (typeof (window as any).snaptr !== 'undefined') {
              // Prepare user data with hashing if available
              let snapUserData = {}
              if (user?.email) {
                const hashedEmail = await hashEmail(user.email)
                snapUserData = {
                  user_email: user.email,  // Snap SDK will hash automatically
                  user_hashed_email: hashedEmail || undefined
                }
              }

              // Format fallback purchase data for Snap
              const fallbackSnapData = {
                price: fallbackValue,
                currency: 'USD',
                transaction_id: session || 'fallback_' + Date.now(),
                item_ids: [usage.plan.toLowerCase()],
                item_category: 'subscription',
                number_items: 1,
                client_dedup_id: fallbackEventId,  // CRITICAL: Must be in event data for deduplication
                ...snapUserData
              }

              // Track PURCHASE event with Snap Pixel
              ;(window as any).snaptr('track', 'PURCHASE', fallbackSnapData)

              if (process.env.NODE_ENV === 'development') {
                console.log('📸 Snap Pixel PURCHASE tracked (fallback):', {
                  price: fallbackSnapData.price,
                  plan: usage.plan,
                  client_dedup_id: fallbackSnapData.client_dedup_id
                })
              }
            }

            setHasTrackedPurchase(true)
          }
        }
      }

      trackPurchaseEvent()
    }

  }, [user?.id, user?.email, usage?.plan, hasTrackedPurchase, purchaseEventId])

  useEffect(() => {
    let current = true;
    setShowPlaybookModal(false);
    // Keep the automatic offer. A PDF checkout return must not offer the same PDF again
    // while its fulfillment webhook is still catching up.
    if (!user?.id || new URLSearchParams(window.location.search).get('pdf') === 'purchased') return;
    const checkPdfStatus = async () => {
      try {
        const { data, error } = await supabase.from('user_profiles').select('purchased_pdf').eq('id', user.id).single();
        if (current && !error && data?.purchased_pdf === false) setShowPlaybookModal(true);
      } catch { /* An unavailable ownership check should not offer a duplicate purchase. */ }
    };
    void checkPdfStatus();
    return () => { current = false; };
  }, [user?.id])

  if (new URLSearchParams(window.location.search).get('pdf') === 'purchased') {
    return <div className="mx-auto max-w-lg px-4 py-8">
      <h1 className="text-center text-3xl font-bold">Your TopClip Playbook</h1>
      <GuideDownload paymentReturn sessionId={new URLSearchParams(window.location.search).get('session_id')} />
      <button type="button" className="min-h-12 w-full text-blue-700 underline" onClick={onShowDashboard}>View Account</button>
    </div>;
  }

  return (
    <div className="relative bg-gradient-to-b from-blue-50 to-white px-4 py-6 sm:py-12">
      <section className="mx-auto w-full max-w-lg overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-xl shadow-blue-900/5" aria-labelledby="success-title">
        <header className="bg-gradient-to-r from-[#007AFF] to-[#4D71FF] px-4 py-7 text-center text-white">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/15">
            {confirmation.loading ? <Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" /> : active ? <CheckCircle className="h-7 w-7" aria-hidden="true" /> : <Clock className="h-7 w-7" aria-hidden="true" />}
          </div>
          <h1 id="success-title" className="mb-2 text-3xl font-bold tracking-tight">Welcome to TopClip!</h1>
          <p className="text-sm text-blue-50" role="status">
            {confirmation.loading ? "We're finishing your setup..." : active ? 'Your subscription is active.' : 'We have not confirmed active access yet.'}
          </p>
        </header>
        <div className="p-5 sm:p-7">
          {active && typeof sourceMinutes === 'number' && sourceMinutes >= 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-4 text-xs sm:text-sm">
              <Clock className="mr-1 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
              <strong>{sourceMinutes.toLocaleString()} source minutes</strong><span>available this period</span>
            </div>
          )}
          {!confirmation.loading && !active && (
            <div className="mb-5 text-center text-sm text-gray-600">
              {confirmation.error && <p role="alert" className="mb-2 text-red-700">{confirmation.error}</p>}
              <p>If you just paid, access may still be updating. You do not need to purchase again.</p>
              {confirmation.needsSignIn ? (
                <a className="mt-3 inline-flex min-h-11 items-center text-blue-600 underline" href={signInPathForDestination('/dashboard')}>Sign in again</a>
              ) : (
                <button type="button" className="mt-3 min-h-11 text-blue-600 underline" onClick={() => void confirmation.refresh()}>Check access again</button>
              )}
              <p><a className="inline-block min-h-11 py-3 text-blue-600 underline" href="mailto:hello@topclip.ai?subject=TopClip%20paid%20access%20help">Contact support for help with paid access</a></p>
            </div>
          )}
          <p className="my-5 text-center text-sm leading-relaxed text-gray-500">Choose a YouTube video and turn your<br className="hidden sm:block" /> favorite moments into clips.</p>
          <button type="button" onClick={() => onStartClipping()} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#007AFF] to-[#4D71FF] px-3 py-4 text-sm font-semibold text-white shadow-md shadow-blue-500/15 hover:brightness-95">
            Start Creating Viral Clips <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
          </button>
          <button type="button" onClick={() => onShowDashboard()} className="mt-3 min-h-12 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50">View Account</button>
        </div>
      </section>
      <PlaybookModal isOpen={showPlaybookModal} onClose={() => setShowPlaybookModal(false)} />
    </div>
  )
}
